(() => {
  const root = document.querySelector('#vm-builder');
  const core = window.VoiceWaveRenderCoreV4;
  if (!root || !core) return;

  const $ = selector => root.querySelector(selector);
  const $$ = selector => [...root.querySelectorAll(selector)];
  const PREVIEW_GEOMETRY = { width: 900, height: 680, safe: { x: 42, y: 36, width: 816, height: 608 } };
  const DEFAULTS = {
    layout: 'classic',
    mode: 'gradient',
    start: '#d99a86',
    middle: '#b56b72',
    end: '#7a4053',
    background: '#ffffff',
    text: '#2b2020',
    font: 'great-vibes-v1',
    productUid: 'voicewave-preview-canvas-v4'
  };
  const PRESETS = Object.freeze(Object.fromEntries(Object.entries(core.V4_PALETTE_PRESETS || {}).map(([id, preset]) => {
    const stops = Array.isArray(preset?.stops) ? preset.stops : [];
    return [id, {
      mode: preset?.mode || 'gradient',
      start: stops[0]?.color,
      middle: stops[1]?.color || stops[0]?.color,
      end: stops.at(-1)?.color || stops[0]?.color,
      background: preset?.background || DEFAULTS.background,
      text: preset?.text || DEFAULTS.text
    }];
  })));
  const state = {
    memoryId: '',
    productUid: '',
    signedMinMax: [],
    appliedSignedMinMax: [],
    appliedTrimStartRatio: 0,
    appliedTrimEndRatio: 1,
    appliedTrimmedSequenceHash: '',
    sourceSequenceHash: '',
    lastDocument: null,
    lastMetrics: null,
    renderToken: 0,
    designError: '',
    syncingLegacy: false,
    miniProofExpanded: true
  };
  const embeddedFontCache = new Map();

  const FONT_ASSET_DATASET_KEYS = Object.freeze({
    'great-vibes-v1': 'v4FontGreatVibes',
    'elegant-script-v1': 'v4FontAllura',
    'parisienne-v1': 'v4FontParisienne',
    'classic-serif-v1': 'v4FontClassicSerif',
    'clean-sans-v1': 'v4FontCleanSans'
  });
  const FONT_FAMILIES = Object.freeze({
    'great-vibes-v1': 'VoiceWave Great Vibes',
    'elegant-script-v1': 'VoiceWave Allura',
    'parisienne-v1': 'VoiceWave Parisienne',
    'classic-serif-v1': 'VoiceWave Abril Fatface',
    'clean-sans-v1': 'VoiceWave Lato'
  });

  async function embeddedFont(fontId) {
    const cached = embeddedFontCache.get(fontId);
    if (cached) return cached;
    const url = root.dataset[FONT_ASSET_DATASET_KEYS[fontId]] || '';
    if (!url) return '';
    const promise = fetch(url).then(async response => {
      if (!response.ok) throw new Error(`Artwork font could not be loaded (${response.status}).`);
      const bytes = new Uint8Array(await response.arrayBuffer());
      let binary = '';
      for (let offset = 0; offset < bytes.length; offset += 0x8000) binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
      return `data:font/ttf;base64,${btoa(binary)}`;
    });
    embeddedFontCache.set(fontId, promise);
    return promise;
  }

  async function svgWithEmbeddedFont(svg, document) {
    const fontId = document.text.fontId;
    const fontIds = [...new Set([fontId, 'clean-sans-v1'])];
    const embedded = await Promise.all(fontIds.map(async id => [id, await embeddedFont(id).catch(() => '')]));
    const fontFaces = embedded.filter(([, data]) => data).map(([id, data]) => `@font-face{font-family:'${FONT_FAMILIES[id]}';src:url('${data}') format('truetype');font-weight:400;font-style:normal}`).join('');
    const titleFamily = FONT_FAMILIES[fontId] || FONT_FAMILIES['great-vibes-v1'];
    const titleFallback = fontId === 'clean-sans-v1' ? 'sans-serif' : fontId === 'classic-serif-v1' ? 'serif' : 'cursive';
    const titleStyle = `text[data-font-id='${fontId}']{font-family:'${titleFamily}',${titleFallback} !important;}`;
    const cleanStyle = `text[data-font-id='clean-sans-v1']{font-family:'${FONT_FAMILIES['clean-sans-v1']}',sans-serif !important;}`;
    const style = `<style>${fontFaces}${titleStyle}${cleanStyle}text{font-synthesis:none}</style>`;
    return svg.replace(/(<svg\b[^>]*>)/u, `$1${style}`);
  }

  const safeHex = value => /^#[0-9a-f]{6}$/iu.test(String(value || ''));
  const normalizedHex = value => safeHex(value) ? String(value).toLowerCase() : '';
  const selectedLayout = () => $('input[name="vm-artwork-layout"]:checked')?.value || DEFAULTS.layout;
  const selectedFontId = () => $('input[name="vm-font"]:checked')?.dataset.v4FontId || DEFAULTS.font;
  const selectedLegacyFont = () => $('input[name="vm-font"]:checked')?.value || 'handwritten';
  const selectedMode = () => $('[data-v4-paint-mode]')?.value || root.dataset.v4PaintMode || DEFAULTS.mode;
  const textValue = field => $(`[data-${field}]`)?.value?.trim() || '';
  const visibleSignedMinMax = () => {
    if (state.appliedSignedMinMax.length >= 24) return state.appliedSignedMinMax;
    if (state.signedMinMax.length < 24) return state.signedMinMax;
    const start = Math.max(0, Math.min(.999, Number(state.appliedTrimStartRatio)));
    const end = Math.max(start + .001, Math.min(1, Number(state.appliedTrimEndRatio)));
    const from = Math.floor(start * state.signedMinMax.length);
    const to = Math.ceil(end * state.signedMinMax.length);
    const sliced = state.signedMinMax.slice(from, Math.max(from + 24, to));
    return sliced.length >= 24 ? sliced : state.signedMinMax;
  };
  const getStops = () => {
    const raw = [$('[data-v4-stop="0"]')?.value, $('[data-v4-stop="0.5"]')?.value, $('[data-v4-stop="1"]')?.value];
    if (raw.some(value => value != null && !safeHex(value))) throw new Error('Each gradient stop must be a valid HEX color.');
    const start = normalizedHex(raw[0]) || DEFAULTS.start;
    const middle = normalizedHex(raw[1]) || DEFAULTS.middle;
    const end = normalizedHex(raw[2]) || DEFAULTS.end;
    return [{ offset: 0, color: start }, { offset: 0.5, color: middle }, { offset: 1, color: end }];
  };
  const solidStop = () => normalizedHex($('[data-v4-stop="0"]')?.value) || DEFAULTS.start;

  function toSignedMinMax(value) {
    if (!Array.isArray(value)) return [];
    const pairs = value.map(entry => {
      if (Array.isArray(entry) && entry.length >= 2) {
        const first = Number(entry[0]); const second = Number(entry[1]);
        if (Number.isFinite(first) && Number.isFinite(second)) return { min: Math.min(first, second), max: Math.max(first, second) };
      }
      if (entry && typeof entry === 'object') {
        const first = Number(entry.min ?? entry.low ?? entry[0]); const second = Number(entry.max ?? entry.high ?? entry[1]);
        if (Number.isFinite(first) && Number.isFinite(second)) return { min: Math.min(first, second), max: Math.max(first, second) };
      }
      const amplitude = Number(entry);
      if (Number.isFinite(amplitude)) {
        const magnitude = Math.min(1, Math.abs(amplitude));
        return { min: -magnitude, max: magnitude };
      }
      return null;
    }).filter(Boolean);
    return pairs.length >= 24 ? pairs : [];
  }

  function extractPeaks(payload) {
    const candidates = [
      payload?.canonicalMinMax,
      payload?.canonicalMedia?.canonicalMinMax,
      payload?.media?.canonicalMinMax,
      payload?.canonicalPeaks,
      payload?.canonicalMedia?.canonicalPeaks,
      payload?.media?.canonicalPeaks,
      payload?.peaks
    ];
    for (const candidate of candidates) {
      const pairs = toSignedMinMax(candidate);
      if (pairs.length) return pairs;
    }
    return [];
  }

  function luminance(hex) {
    const rgb = [0, 2, 4].map(offset => Number.parseInt(hex.slice(1 + offset, 3 + offset), 16) / 255);
    return rgb.map(value => value <= .03928 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4).reduce((sum, value, index) => sum + value * [0.2126, 0.7152, 0.0722][index], 0);
  }

  function contrastRatio(first, second) {
    const a = luminance(first); const b = luminance(second);
    return (Math.max(a, b) + .05) / (Math.min(a, b) + .05);
  }

  function effectiveTextColor(background, stops, explicit) {
    const auto = $('[data-auto-text]')?.checked !== false;
    if (!auto && safeHex(explicit)) return explicit.toLowerCase();
    // Text is laid over the independent background field. Wave stops are
    // validated separately by renderer v4, so a light paper background does
    // not make a dark gradient incorrectly fail the text gate.
    const surfaces = [background];
    const candidates = ['#fff8ec', '#28221e'];
    return candidates.sort((left, right) => {
      const leftScore = Math.min(...surfaces.map(surface => contrastRatio(left, surface)));
      const rightScore = Math.min(...surfaces.map(surface => contrastRatio(right, surface)));
      return rightScore - leftScore;
    })[0];
  }

  function colorState() {
    const mode = selectedMode() === 'solid' ? 'solid' : 'gradient';
    const background = normalizedHex($('[data-bg]')?.value) || normalizedHex($('[data-v4-background]')?.value) || DEFAULTS.background;
    const stops = mode === 'solid' ? [{ offset: 0, color: solidStop() }] : getStops();
    const explicit = normalizedHex($('[data-text-color]')?.value) || normalizedHex($('[data-v4-text-color]')?.value) || DEFAULTS.text;
    const text = effectiveTextColor(background, stops, explicit);
    return { mode, stops, background, text };
  }

  function updateContrastStatus(colors) {
    const reason = $('[data-v4-contrast-reason]');
    const node = $('[data-contrast-status]');
    const ratio = contrastRatio(colors.text, colors.background);
    const auto = $('[data-auto-text]')?.checked !== false;
    const message = ratio >= 3 ? `${ratio.toFixed(1)}:1 minimum text contrast${auto ? ' · adjusted automatically' : ''}.` : `Text contrast is ${ratio.toFixed(1)}:1. Choose a darker or lighter text color.`;
    if (reason) reason.textContent = message;
    if (node) { node.textContent = message; node.dataset.state = ratio >= 3 ? 'ok' : 'error'; }
    return ratio;
  }

  function syncLegacyControls(colors) {
    if (state.syncingLegacy) return;
    state.syncingLegacy = true;
    const wave = $('[data-color]');
    const background = $('[data-bg]');
    const text = $('[data-text-color]');
    const values = [[wave, colors.stops[0].color], [background, colors.background], [text, colors.text]];
    values.forEach(([input, value]) => {
      if (!input || input.value.toLowerCase() === value) return;
      input.value = value;
    });
    const legacyBackground = $('[data-v4-legacy-bg]');
    const legacyText = $('[data-v4-legacy-text]');
    if (legacyBackground) legacyBackground.value = colors.background;
    if (legacyText) legacyText.value = colors.text;
    root.dataset.v4PaintMode = colors.mode;
    root.dataset.v4Layout = selectedLayout();
    state.syncingLegacy = false;
  }

  function setMode(mode) {
    const normalized = mode === 'solid' ? 'solid' : 'gradient';
    const select = $('[data-v4-paint-mode]');
    if (select) select.value = normalized;
    $$('[data-v4-paint-button]').forEach(button => {
      const active = button.dataset.v4PaintButton === normalized;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    root.dataset.v4PaintMode = normalized;
  }

  function setColorInput(selector, value) {
    const input = $(selector);
    if (input && safeHex(value)) input.value = value.toLowerCase();
  }

  function applyPreset(button) {
    const preset = PRESETS[button?.dataset.paletteId] || {
      start: button?.dataset.gradientStart,
      middle: button?.dataset.gradientMiddle,
      end: button?.dataset.gradientEnd,
      background: button?.dataset.background,
      text: button?.dataset.text
    };
    if (!preset || !safeHex(preset.start)) return;
    setMode(preset.mode || button?.dataset.paintMode || 'gradient');
    setColorInput('[data-v4-stop="0"]', preset.start);
    setColorInput('[data-v4-stop="0.5"]', preset.middle || preset.start);
    setColorInput('[data-v4-stop="1"]', preset.end || preset.start);
    setColorInput('[data-bg]', preset.background || DEFAULTS.background);
    setColorInput('[data-text-color]', preset.text || DEFAULTS.text);
    $$('[data-palette]').forEach(item => {
      const active = item === button;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });
    root.dataset.v4Palette = button?.dataset.paletteId || 'custom';
  }

  function buildDocument(design = {}, peaks = visibleSignedMinMax()) {
    const colors = colorState();
    const layout = selectedLayout();
    const durationMs = Math.max(1, Number(design.durationMs || design.trimEndMs || 1));
    const startMs = Math.max(0, Math.min(durationMs, Number(design.trimStartMs || 0)));
    const endMs = Math.max(startMs + 1, Math.min(durationMs, Number(design.trimEndMs || durationMs)));
    const productUid = String(design.productUid || state.productUid || root.dataset.productUid || DEFAULTS.productUid);
    const hash = peaks.length ? core.canonicalPeaksHashV4(peaks) : '00000000';
    return {
      schemaVersion: 'voicewave-design-v4',
      rendererVersion: 'waveform-renderer-v4',
      memoryId: String(design.memoryId || state.memoryId || 'preview-memory-v4'),
      productUid,
      layout,
      presetId: layout === 'classic' ? 'classic-gallery-wave-v3' : 'user-selected-v1',
      trim: { startMs, endMs },
      peaks: { revision: 'voicewave-canonical-minmax-v4', sha256: hash },
      paint: { mode: colors.mode, mapping: 'along-time', stops: colors.stops },
      background: colors.background,
      text: { title: textValue('title'), kicker: '', date: textValue('date'), fontId: selectedFontId(), color: colors.text }
    };
  }

  function updateCounters(document) {
    const layout = document.layout;
    const titleLimit = layout === 'classic' ? 52 : layout === 'heart' ? 28 : 34;
    const recommendations = {
      title: `For the most balanced ${layout[0].toUpperCase()}${layout.slice(1)} artwork, keep it around ${titleLimit} characters or fewer${layout === 'classic' ? '.' : '; longer messages may use two lines.'}`,
      date: 'Around 32 characters usually gives the best spacing.'
    };
    const titleHelp = $('[data-v4-title-help]');
    if (titleHelp) titleHelp.textContent = layout === 'classic' ? 'Classic places your message below the waveform.' : `${layout[0].toUpperCase()}${layout.slice(1)} places your message inside the shape.`;
    ['title', 'date'].forEach(field => {
      const value = document.text[field] || '';
      const count = $(`[data-v4-count="${field}"]`);
      const recommendation = $(`[data-v4-recommended="${field}"]`);
      if (count) count.textContent = String([...value].length);
      if (recommendation) recommendation.textContent = recommendations[field];
      const fieldError = $(`[data-v4-field-error="${field}"]`);
      if (fieldError && !fieldError.textContent) fieldError.hidden = true;
    });
  }

  function showTextMeasurement(document) {
    const status = $('[data-v4-text-status]');
    if (!status) return { blocked: false, ratio: 1 };
    ['title', 'date'].forEach(field => {
      const error = $(`[data-v4-field-error="${field}"]`);
      if (error) { error.textContent = ''; error.hidden = true; }
    });
    const hasText = Object.values(document.text).some((value, index) => index < 3 && value);
    if (!hasText) {
      status.textContent = 'Text is optional. You can enter a message or continue with pure soundwave art.';
      status.dataset.state = 'ok';
      return { blocked: false, missing: false, ratio: 1 };
    }
    try {
      const canonical = core.canonicalizeDesignDocumentV4(document);
      const measured = core.measureTextLayoutV4(canonical, PREVIEW_GEOMETRY);
      const ratio = Number(measured.textVisualScale?.ratio || 1);
      if (measured.status === 'blocked') {
        const field = /^(title|kicker|date)\b/iu.exec(measured.error || '')?.[1];
        if (field) {
          const error = $(`[data-v4-field-error="${field}"]`);
          if (error) { error.hidden = false; error.textContent = measured.error; }
        }
        status.textContent = measured.error || 'Shorten the text to fit the measured safe area.';
        status.dataset.state = 'error';
        return { blocked: true, ratio };
      }
      const warning = ratio < .82;
      const recommended = document.layout === 'classic' ? 52 : document.layout === 'heart' ? 28 : 34;
      const overRecommendation = [...document.text.title].length > recommended || [...document.text.date].length > 32;
      status.textContent = warning ? 'This wording is close to the smallest printable size. Shortening it will give the artwork more breathing room.' : overRecommendation ? 'Your wording still fits, but a shorter message will usually create a more balanced, elegant composition.' : `${document.layout === 'classic' ? 'Classic places the message below the waveform.' : `${document.layout[0].toUpperCase()}${document.layout.slice(1)} centers the message inside the shape.`} Text is measured with the selected production font.`;
      status.dataset.state = warning || overRecommendation ? 'warning' : 'ok';
      return { blocked: false, ratio };
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : 'Text needs attention before the proof can be generated.';
      status.dataset.state = 'error';
      return { blocked: true, ratio: 0 };
    }
  }

  function setValidation(message, isError = false) {
    const node = $('[data-v4-validation]');
    if (!node) return;
    node.textContent = message;
    node.hidden = !message;
    node.dataset.state = isError ? 'error' : 'ok';
  }

  function setNextLocked(locked) {
    $$('[data-next="3"], [data-generate-proof]').forEach(button => {
      if (locked) {
        button.disabled = true;
        button.dataset.v4Locked = 'true';
      } else if (button.dataset.v4Locked === 'true') {
        button.disabled = false;
        delete button.dataset.v4Locked;
      }
    });
    const hint = $('[data-v4-next-help]');
    if (hint) hint.textContent = locked ? 'Resolve the highlighted text or contrast issue to continue.' : 'Choose a product to see the exact artwork face and price.';
  }

  function preserveCustomizerScroll(callback) {
    const scrollContainer = document.querySelector('.page-wrapper');
    const usesThemeScroller = scrollContainer instanceof HTMLElement;
    const top = usesThemeScroller ? scrollContainer.scrollTop : window.scrollY;
    const left = usesThemeScroller ? scrollContainer.scrollLeft : window.scrollX;
    callback();
    const restore = () => {
      if (usesThemeScroller) {
        scrollContainer.scrollTo({ top, left, behavior: 'instant' });
        // Horizon scrolls `.page-wrapper` on desktop. A focused visually-hidden
        // radio can still move the outer window and expose blank space below it.
        if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
      else window.scrollTo({ top, left, behavior: 'instant' });
    };
    restore();
    requestAnimationFrame(restore);
    window.setTimeout(restore, 120);
    window.setTimeout(restore, 400);
  }

  async function drawSharedPreviewLegacy(document, metrics) {
    const canvas = $('[data-art-waveform]');
    if (!(canvas instanceof HTMLCanvasElement) || !state.signedMinMax.length) return;
    const board = $('[data-view-panel="design"]');
    const hadValidCanvas = canvas.width === PREVIEW_GEOMETRY.width && canvas.height === PREVIEW_GEOMETRY.height && !canvas.hidden;
    const token = ++state.renderToken;
    let previewSvg;
    try { previewSvg = await svgWithEmbeddedFont(metrics.svg, document); }
    catch (error) {
      setValidation(error instanceof Error ? error.message : 'Artwork font could not be loaded.', true);
      setNextLocked(true);
      return;
    }
    if (token !== state.renderToken) return;
    const blob = new Blob([previewSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      if (token !== state.renderToken) { URL.revokeObjectURL(url); return; }
      canvas.width = PREVIEW_GEOMETRY.width;
      canvas.height = PREVIEW_GEOMETRY.height;
      canvas.hidden = false;
      if (board) board.dataset.v4Rendered = 'true';
      canvas.getContext('2d')?.drawImage(image, 0, 0, PREVIEW_GEOMETRY.width, PREVIEW_GEOMETRY.height);
      ['[data-preview-kicker]', '[data-preview-title]', '[data-preview-date]'].forEach(selector => {
        const legacyText = $(selector);
        if (legacyText) legacyText.hidden = true;
      });
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      if (!hadValidCanvas) canvas.hidden = true;
      setValidation('The artwork preview could not be drawn. Please retry the upload.', true);
      setNextLocked(true);
    };
    image.src = url;
  }

  async function drawSharedPreview(document, metrics) {
    const canvas = $('[data-art-waveform]');
    if (!(canvas instanceof HTMLCanvasElement) || !state.signedMinMax.length) return;
    const board = $('[data-view-panel="design"]');
    const hadValidCanvas = canvas.width === PREVIEW_GEOMETRY.width && canvas.height === PREVIEW_GEOMETRY.height && !canvas.hidden;
    const token = ++state.renderToken;
    // Clear stale pixels immediately so a background change cannot leave the
    // previous white proof visible while the next SVG is loading.
    canvas.width = PREVIEW_GEOMETRY.width;
    canvas.height = PREVIEW_GEOMETRY.height;
    const context = canvas.getContext('2d');
    if (context) {
      context.fillStyle = document.background;
      context.fillRect(0, 0, PREVIEW_GEOMETRY.width, PREVIEW_GEOMETRY.height);
    }
    let finalRenderStarted = false;
    const drawSvg = (svg, final = false) => new Promise(resolve => {
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const image = new Image();
      image.onload = () => {
        if (token !== state.renderToken || (!final && finalRenderStarted)) { URL.revokeObjectURL(url); resolve(false); return; }
        if (final) finalRenderStarted = true;
        canvas.width = PREVIEW_GEOMETRY.width;
        canvas.height = PREVIEW_GEOMETRY.height;
        canvas.hidden = false;
        // Hide the upload-wait overlay now that the real waveform is rendered
        const uploadWait = $('[data-upload-wait]');
        if (uploadWait) uploadWait.classList.add('is-hidden');
        if (board) board.dataset.v4Rendered = 'true';
        canvas.getContext('2d')?.drawImage(image, 0, 0, PREVIEW_GEOMETRY.width, PREVIEW_GEOMETRY.height);
        ['[data-preview-kicker]', '[data-preview-title]', '[data-preview-date]'].forEach(selector => {
          const legacyText = $(selector);
          if (legacyText) legacyText.hidden = true;
        });
        URL.revokeObjectURL(url);
        resolve(true);
      };
      image.onerror = () => { URL.revokeObjectURL(url); resolve(false); };
      image.src = url;
    });

    // Draw the raw SVG immediately so changing the crop is visible even when
    // embedded font assets are slow. Upgrade to the embedded-font render when
    // those assets arrive; the final proof still uses the canonical renderer.
    const fastDraw = drawSvg(metrics.svg);
    const embeddedSvg = svgWithEmbeddedFont(metrics.svg, document);
    await fastDraw;
    let previewSvg;
    try { previewSvg = await embeddedSvg; }
    catch (error) {
      if (token === state.renderToken && !hadValidCanvas) {
        setValidation(error instanceof Error ? error.message : 'Artwork font could not be loaded.', true);
        setNextLocked(true);
      }
      return;
    }
    if (token !== state.renderToken || previewSvg === metrics.svg) return;
    await drawSvg(previewSvg, true);
  }

  function keepLastValidPreview(board) {
    const hasValidPreview = Boolean(state.lastDocument && $('[data-art-waveform]')?.hidden === false);
    if (board) {
      if (hasValidPreview) board.dataset.v4Rendered = 'true';
      else delete board.dataset.v4Rendered;
    }
    ['[data-preview-kicker]', '[data-preview-title]', '[data-preview-date]'].forEach(selector => {
      const legacyText = $(selector);
      if (legacyText) legacyText.hidden = hasValidPreview;
    });
  }

  function clearPreviewCanvas(background) {
    state.renderToken += 1;
    const canvas = $('[data-art-waveform]');
    if (!(canvas instanceof HTMLCanvasElement)) return null;
    canvas.width = PREVIEW_GEOMETRY.width;
    canvas.height = PREVIEW_GEOMETRY.height;
    const context = canvas.getContext('2d');
    if (context) {
      context.fillStyle = background;
      context.fillRect(0, 0, PREVIEW_GEOMETRY.width, PREVIEW_GEOMETRY.height);
    }
    canvas.hidden = false;
    return canvas;
  }

  function renderPreview() {
    const trimEditing = root.dataset.trimEditing === 'true';
    let colors;
    try { colors = colorState(); }
    catch (error) {
      state.designError = error instanceof Error ? error.message : 'Choose valid artwork colors.';
      setValidation(state.designError, true);
      setNextLocked(true);
      return;
    }
    syncLegacyControls(colors);
    const document = buildDocument();
    updateCounters(document);
    const contrast = updateContrastStatus(colors);
    const textResult = showTextMeasurement(document);
    const nextBlocked = contrast < 3 || textResult.blocked;
    const previewBlocked = !trimEditing && (contrast < 3 || (textResult.blocked && !textResult.missing));
    state.designError = nextBlocked ? ($('[data-v4-text-status]')?.textContent || 'This design needs attention.') : '';
    setNextLocked(nextBlocked);
    const board = $('[data-view-panel="design"]');
    if (board) {
      board.style.background = colors.background;
      board.style.setProperty('--vm-artwork-background', colors.background);
      board.style.color = colors.text;
      board.dataset.artLayout = document.layout;
      board.dataset.font = selectedLegacyFont();
      board.dataset.v4FontId = selectedFontId();
      board.style.setProperty('--vm-wave-color', colors.stops[0].color);
      board.style.setProperty('--vm-wave-gradient', `linear-gradient(90deg, ${colors.stops.map(stop => stop.color).join(', ')})`);
    }
    root.dataset.v4PaintMode = colors.mode;
    root.dataset.v4Layout = document.layout;
    if (previewBlocked) {
      const boardBlocked = $('[data-view-panel="design"]');
      const previousBackground = state.lastDocument?.background;
      keepLastValidPreview(boardBlocked);
      // Do not leave a previous white proof visible after the customer changes
      // the artwork background to an invalid/low-contrast combination. Show
      // the current surface with the validation message instead of stale art.
      if (previousBackground !== document.background) {
        if (state.signedMinMax.length) {
          const canvas = clearPreviewCanvas(document.background);
          if (canvas) {
            if (boardBlocked) delete boardBlocked.dataset.v4Rendered;
          }
        }
      }
      setValidation(state.designError || 'This design needs attention before the proof can be generated.', true);
      return;
    }
    const previewPeaks = trimEditing ? state.signedMinMax : visibleSignedMinMax();
    if (!previewPeaks.length) {
      state.lastDocument = document;
      const boardWithoutPeaks = $('[data-view-panel="design"]');
      if (boardWithoutPeaks) delete boardWithoutPeaks.dataset.v4Rendered;
      setValidation(nextBlocked ? state.designError : 'Ready: text and color settings are measured with the shared renderer v4.', nextBlocked);
      return;
    }
    try {
      const previewDocument = trimEditing
        ? { ...document, layout: 'classic', text: { kicker: '', title: '\u200B', date: '' } }
        : document;
      const rendered = core.renderWaveformDocumentV4({ document: previewDocument, signedMinMax: previewPeaks, geometry: PREVIEW_GEOMETRY, sourceSequenceHash: state.sourceSequenceHash || core.canonicalPeaksHashV4(previewPeaks) });
      state.lastDocument = trimEditing ? null : rendered.document;
      state.lastMetrics = rendered.metrics;
      state.designError = '';
      drawSharedPreview(previewDocument, { svg: rendered.svg });
      root.dataset.v4GeometryFingerprint = rendered.geometryFingerprint;
      root.dataset.v4StyleFingerprint = rendered.styleFingerprint;
      root.dataset.v4FontId = rendered.metrics.fontId;
      root.dataset.v4FontHash = rendered.metrics.fontHash;
      setValidation(trimEditing ? 'Drag either scissors handle to keep the sound you want.' : 'Ready: shared renderer v4 is measuring this design.', false);
      if (!trimEditing) root.dispatchEvent(new CustomEvent('vm:v4-document-change', { detail: { document: rendered.document, metrics: rendered.metrics } }));
    } catch (error) {
      const previousBackground = state.lastDocument?.background;
      state.lastDocument = null;
      const boardWithError = $('[data-view-panel="design"]');
      keepLastValidPreview(boardWithError);
      if (previousBackground !== document.background && state.signedMinMax.length) {
        const canvas = clearPreviewCanvas(document.background);
        if (canvas && boardWithError) delete boardWithError.dataset.v4Rendered;
      }
      state.designError = error instanceof Error ? error.message : 'This design needs attention.';
      setValidation(state.designError, true);
      setNextLocked(true);
    }
  }

  async function captureResponse(url, response) {
    if (!/\/(status|peaks|upload)(?:\?|\/|$)/iu.test(url)) return;
    const payload = await response.clone().json().catch(() => null);
    if (!payload) return;
    const incomingMemoryId = payload.memoryId ? String(payload.memoryId) : '';
    if (payload.operation === 'init' && incomingMemoryId) {
      state.memoryId = incomingMemoryId;
      state.signedMinMax = [];
      state.sourceSequenceHash = '';
    } else if (incomingMemoryId && state.memoryId && incomingMemoryId !== state.memoryId) {
      return;
    } else if (incomingMemoryId) state.memoryId = incomingMemoryId;
    const peaks = extractPeaks(payload);
    if (peaks.length) state.signedMinMax = peaks;
    if (payload.sourceSequenceHash) state.sourceSequenceHash = String(payload.sourceSequenceHash);
    if (state.signedMinMax.length) renderPreview();
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    if (init?.method?.toUpperCase() === 'POST' && /\/design(?:\?|$)/iu.test(url) && typeof init.body === 'string' && state.signedMinMax.length) {
      try {
        const body = JSON.parse(init.body);
        if (body.design && typeof body.design === 'object') {
          const selectedVariant = window.VM_SELECTED_VARIANT || {};
          const variantId = String(body.design.shopifyVariantId || selectedVariant.shopifyVariantId || root.dataset.shopifyVariantId || '');
          state.productUid = String(body.design.productUid || selectedVariant.productUid || state.productUid);
          const canonicalDesign = buildDocument({ ...body.design, memoryId: body.memoryId || state.memoryId, productUid: body.design.productUid || selectedVariant.productUid || state.productUid });
          // Shopify Variant identity is transport metadata for the server-side
          // resolver; the v4 canonical document schema intentionally excludes it.
          if (variantId) canonicalDesign.shopifyVariantId = variantId;
          body.design = canonicalDesign;
          init = { ...init, body: JSON.stringify(body) };
        }
      } catch (_) { /* the original request will surface its own error */ }
    }
    const response = await originalFetch(input, init);
    await captureResponse(url, response);
    return response;
  };

  root.addEventListener('vm:canonical-reset', event => {
    state.memoryId = String(event.detail?.memoryId || '');
    state.signedMinMax = [];
    state.sourceSequenceHash = '';
    state.lastDocument = null;
    state.renderToken += 1;
    // New upload started — restore the overlay and reset canvas
    const waitOverlay = $('[data-upload-wait]');
    if (waitOverlay) {
      waitOverlay.classList.remove('is-hidden');
      const lbl = waitOverlay.querySelector('[data-upload-wait-label]');
      const sub = waitOverlay.querySelector('[data-upload-wait-sub]');
      if (lbl) { lbl.childNodes[0].textContent = 'Uploading your recording'; }
      if (sub) sub.textContent = 'Hold on — we\'re receiving your audio';
    }
    const resetCanvas = $('[data-art-waveform]');
    if (resetCanvas instanceof HTMLCanvasElement) resetCanvas.hidden = true;
  });

  // Keep overlay label in sync with right-panel upload-status changes
  const uploadStatusEl = $('[data-upload-status]');
  if (uploadStatusEl) {
    new MutationObserver(() => {
      const waitOverlay = $('[data-upload-wait]');
      if (!waitOverlay || waitOverlay.classList.contains('is-hidden')) return;
      const state_ = uploadStatusEl.dataset.state || '';
      const lbl = waitOverlay.querySelector('[data-upload-wait-label]');
      const sub = waitOverlay.querySelector('[data-upload-wait-sub]');
      if (!lbl || !sub) return;
      if (state_ === 'uploading') {
        lbl.childNodes[0].textContent = 'Uploading your recording';
        sub.textContent = 'Hold on — we\'re receiving your audio';
      } else if (state_ === 'processing' || state_ === 'ready') {
        lbl.childNodes[0].textContent = 'Analyzing your recording';
        sub.textContent = 'Mapping the unique peaks of your sound';
      }
    }).observe(uploadStatusEl, { attributes: true, attributeFilter: ['data-state'] });
  }

  $$('[data-v4-paint-button]').forEach(button => button.addEventListener('click', () => preserveCustomizerScroll(() => { setMode(button.dataset.v4PaintButton); renderPreview(); })));
  $('[data-v4-paint-mode]')?.addEventListener('change', event => preserveCustomizerScroll(() => { setMode(event.target.value); renderPreview(); }));
  $$('[data-palette]').forEach(button => button.addEventListener('click', () => preserveCustomizerScroll(() => { applyPreset(button); renderPreview(); })));
  $$('[data-v4-stop], [data-bg], [data-text-color]').forEach(input => input.addEventListener('input', () => preserveCustomizerScroll(() => { root.dataset.v4Palette = 'custom'; $$('[data-palette]').forEach(item => { item.classList.remove('is-active'); item.setAttribute('aria-pressed', 'false'); }); renderPreview(); })));
  $$('[data-auto-text]').forEach(input => input.addEventListener('change', () => preserveCustomizerScroll(renderPreview)));
  $$('[data-title], [data-date], [data-crop-start], [data-crop-end]').forEach(input => { input.addEventListener('input', renderPreview); input.addEventListener('change', renderPreview); });
  $$('input[name="vm-artwork-layout"]').forEach(input => input.addEventListener('change', () => preserveCustomizerScroll(() => { root.dataset.v4Layout = selectedLayout(); renderPreview(); })));
  $$('input[name="vm-font"]').forEach(input => input.addEventListener('change', () => preserveCustomizerScroll(renderPreview)));
  const previewToggle = $('[data-v4-mini-proof-toggle]');
  const setMobilePreviewOpen = open => {
    state.miniProofExpanded = Boolean(open);
    root.classList.toggle('vm-mobile-preview-open', state.miniProofExpanded);
    previewToggle?.setAttribute('aria-expanded', String(state.miniProofExpanded));
    const label = $('[data-v4-preview-toggle-label]');
    const hint = $('[data-v4-preview-toggle-hint]');
    if (label) label.textContent = state.miniProofExpanded ? 'Close ×' : 'View large ↗';
    if (hint) hint.textContent = state.miniProofExpanded ? 'Return to editing' : 'Inspect artwork';
    document.documentElement.style.overflow = state.miniProofExpanded ? 'hidden' : '';
    requestAnimationFrame(renderPreview);
  };
  previewToggle?.addEventListener('click', () => setMobilePreviewOpen(!state.miniProofExpanded));
  $('.vm-preview-stage')?.addEventListener('click', event => {
    if (window.innerWidth < 761 && !event.target.closest('button, input, [data-mobile-generation-status]') && !state.miniProofExpanded) setMobilePreviewOpen(true);
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && state.miniProofExpanded) setMobilePreviewOpen(false);
  });

  root.dataset.mobileTool = root.dataset.mobileTool || 'shape';
  const selectMobileTool = key => {
    if (key === 'product') {
      const next = $('[data-next="3"]');
      if (next && !next.disabled) next.click();
      else {
        root.dataset.mobileTool = 'text';
        $$('[data-mobile-tool-target]').forEach(item => item.classList.toggle('is-active', item.dataset.mobileToolTarget === 'text'));
        $('[data-v4-validation]')?.removeAttribute('hidden');
      }
      return;
    }
    root.dataset.mobileTool = key;
    $$('[data-mobile-tool-target]').forEach(item => item.classList.toggle('is-active', item.dataset.mobileToolTarget === key));
    const panel = $('[data-design-controls]');
    const scroller = document.querySelector('.page-wrapper');
    if (panel && scroller instanceof HTMLElement && scroller.scrollHeight > scroller.clientHeight + 2) {
      const previewHeight = $('.vm-preview')?.getBoundingClientRect().height || 196;
      const top = scroller.scrollTop + panel.getBoundingClientRect().top - previewHeight - 56;
      scroller.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    } else if (panel) {
      const previewHeight = $('.vm-preview')?.getBoundingClientRect().height || 196;
      const top = window.scrollY + panel.getBoundingClientRect().top - previewHeight - 56;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    }
  };
  $$('[data-mobile-tool-target]').forEach(button => button.addEventListener('click', () => selectMobileTool(button.dataset.mobileToolTarget)));
  const swipePanel = $('[data-design-controls]');
  let swipeStart = null;
  swipePanel?.addEventListener('touchstart', event => {
    const touch = event.changedTouches?.[0];
    if (touch) swipeStart = { x: touch.clientX, y: touch.clientY };
  }, { passive: true });
  swipePanel?.addEventListener('touchend', event => {
    const touch = event.changedTouches?.[0];
    if (!touch || !swipeStart) return;
    const dx = touch.clientX - swipeStart.x;
    const dy = touch.clientY - swipeStart.y;
    swipeStart = null;
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    const tools = ['shape', 'color', 'text'];
    const current = Math.max(0, tools.indexOf(root.dataset.mobileTool));
    selectMobileTool(tools[Math.max(0, Math.min(tools.length - 1, current + (dx < 0 ? 1 : -1)))]);
  }, { passive: true });
  const pageScroller = document.querySelector('.page-wrapper');
  const updateScrolledState = () => {
    const top = pageScroller instanceof HTMLElement ? pageScroller.scrollTop : window.scrollY;
    root.classList.toggle('vm-v4-is-scrolled', window.innerWidth < 761 && top > 150);
  };
  (pageScroller || window).addEventListener('scroll', updateScrolledState, { passive: true });
  window.addEventListener('resize', () => requestAnimationFrame(renderPreview));
  root.addEventListener('vm:canonical-ready', event => {
    const detail = event.detail || {};
    state.memoryId = String(detail.memoryId || state.memoryId);
    const peaks = extractPeaks(detail);
    if (peaks.length) state.signedMinMax = peaks;
    state.appliedSignedMinMax = [];
    state.appliedTrimStartRatio = 0;
    state.appliedTrimEndRatio = 1;
    state.appliedTrimmedSequenceHash = '';
    state.sourceSequenceHash = String(detail.sequenceHash || state.sourceSequenceHash);
    renderPreview();
  });
  root.addEventListener('vm:crop-applied', event => {
    const detail = event.detail || {};
    const durationMs = Math.max(1, Number(detail.durationMs || 1));
    const startMs = Math.max(0, Math.min(durationMs, Number(detail.trimStartMs || 0)));
    const endMs = Math.max(startMs + 1, Math.min(durationMs, Number(detail.trimEndMs || durationMs)));
    const peaks = extractPeaks(detail);
    state.appliedSignedMinMax = peaks;
    state.appliedTrimStartRatio = startMs / durationMs;
    state.appliedTrimEndRatio = endMs / durationMs;
    state.appliedTrimmedSequenceHash = String(detail.trimmedSequenceHash || (peaks.length ? core.canonicalPeaksHashV4(peaks) : ''));
    state.sourceSequenceHash = state.appliedTrimmedSequenceHash || state.sourceSequenceHash;
    renderPreview();
  });
  root.addEventListener('vm:crop-reset', () => {
    state.appliedSignedMinMax = [];
    state.appliedTrimStartRatio = 0;
    state.appliedTrimEndRatio = 1;
    state.appliedTrimmedSequenceHash = '';
    renderPreview();
  });
  root.addEventListener('vm:v4-render-request', renderPreview);
  if (root.voiceWaveCanonicalSnapshot) {
    const snapshot = root.voiceWaveCanonicalSnapshot;
    state.memoryId = String(snapshot.memoryId || state.memoryId);
    const peaks = extractPeaks(snapshot);
    if (peaks.length) state.signedMinMax = peaks;
    state.sourceSequenceHash = String(snapshot.sequenceHash || state.sourceSequenceHash);
  }
  if (root.voiceWaveCropSnapshot) {
    root.dispatchEvent(new CustomEvent('vm:crop-applied', { detail: root.voiceWaveCropSnapshot }));
  }
  const initialPalette = $('[data-palette-id="rose-gold"]');
  if (initialPalette) applyPreset(initialPalette);
  renderPreview();
})();
